import classNames from 'classnames';
import Button from '@alltrails/denali/components/Button';
import { FormattedMessage } from 'react-intl';
import WebProPurchaseLocation from '@alltrails/amplitude/enums/WebProPurchaseLocation';
import type CurrencyCode from '@alltrails/core/types/CurrencyCode';
import { ComponentProps, Dispatch, SetStateAction, useEffect, useState } from 'react';
import type Plan from '@alltrails/core/types/Plan';
import type User from '@alltrails/core/types/User';
import useUser from '@alltrails/context/hooks/useUser';
import type Coupon from '@alltrails/types/Coupon';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import type { ApplePayConfig } from '@recurly/recurly-js';
import logPurchaseButtonTapped from '@alltrails/amplitude/events/logPurchaseButtonTapped';
import getPurchaseScreenTypePlan from '../../utils/getPurchaseScreenTypePlan';
import ProResponse from '../../types/ProResponse';
import RadioPaymentButtons from './RadioPaymentButtons';
import styles from './CheckoutModalFooter.module.scss';
import { PaymentOptionValues } from './RadioPaymentForm/PaymentOptions';

type Props = {
  applePayConfig: ApplePayConfig;
  coupon?: Coupon;
  currencyCode: CurrencyCode;
  isBaseVariant: boolean;
  onApplePayToken: ComponentProps<typeof RadioPaymentButtons>['onApplePayToken'];
  onGooglePayToken: ComponentProps<typeof RadioPaymentButtons>['onGooglePayToken'];
  onPayPalToken: ComponentProps<typeof RadioPaymentButtons>['onPayPalToken'];
  plan: Plan;
  purchaseTier: PurchaseTier;
  selectedPaymentType?: PaymentOptionValues;
  setErrors: Dispatch<SetStateAction<ProResponse['errors'] | undefined>>;
  setSubmitting: Dispatch<SetStateAction<boolean>>;
  submitting: boolean;
  submittingCreditCard: boolean;
  trigger: UpsellTrigger;
  webProPurchaseLocation: WebProPurchaseLocation;
};

function getBaseVariantButtonText(user: User, coupon?: Coupon) {
  if (coupon && coupon.type !== 'FreeTrialCoupon') {
    return <FormattedMessage defaultMessage="Save today" />;
  }

  return user.isPromoEligible ? <FormattedMessage defaultMessage="Start your free trial" /> : <FormattedMessage defaultMessage="Upgrade" />;
}

export default function CheckoutModalFooter({
  applePayConfig,
  isBaseVariant,
  coupon,
  currencyCode,
  selectedPaymentType,
  webProPurchaseLocation,
  onApplePayToken,
  plan,
  onPayPalToken,
  setErrors,
  setSubmitting,
  submitting,
  submittingCreditCard,
  onGooglePayToken,
  purchaseTier,
  trigger
}: Props) {
  const [canScrollDown, setCanScrollDown] = useState(false);

  const user = useUser()!;

  const checkScrollPosition = () => {
    const container = document.querySelector('#modal-scroll-container');
    if (container) {
      const { scrollTop, scrollHeight, clientHeight } = container;
      setCanScrollDown(clientHeight + scrollTop + 1 < scrollHeight); // + 1 to deal with decimals at misc screen heights
    }
  };

  // Hide footer border when scrolled to the bottom of the container.
  // This logic is copied from Modal.tsx
  useEffect(() => {
    const container = document.querySelector('#modal-scroll-container');
    if (container) {
      container.addEventListener('scroll', checkScrollPosition);
      checkScrollPosition();
    }
    return () => {
      if (container) {
        container.removeEventListener('scroll', checkScrollPosition);
      }
    };
  }, []);

  const productType = purchaseTier === 'peak' ? 'peak' : 'plus';

  return (
    <div className={classNames(styles.footer, { [styles.canScrollDown]: canScrollDown })}>
      <div className={styles.footerContent}>
        {isBaseVariant ? (
          <Button
            className={styles.button}
            loading={submittingCreditCard}
            onClick={() => {
              logPurchaseButtonTapped({
                payment_method: 'credit_card',
                product_type: productType,
                purchase_screen_type_plan: getPurchaseScreenTypePlan(plan, coupon),
                upsell_trigger: trigger,
                web_purchase_location: webProPurchaseLocation
              });
            }}
            testId="submit"
            text={getBaseVariantButtonText(user, coupon)}
            type="submit"
            variant="primary"
            width="full"
          />
        ) : (
          <RadioPaymentButtons
            applePayConfig={applePayConfig}
            buttonClassName={styles.button}
            coupon={coupon}
            currencyCode={currencyCode}
            onApplePayToken={onApplePayToken}
            onGooglePayToken={onGooglePayToken}
            onPayPalToken={onPayPalToken}
            plan={plan}
            purchaseTier={purchaseTier}
            selectedPaymentType={selectedPaymentType}
            setErrors={setErrors}
            setSubmitting={setSubmitting}
            submitting={submitting}
            submittingCreditCard={submittingCreditCard}
            trigger={trigger}
            webProPurchaseLocation={webProPurchaseLocation}
          />
        )}
      </div>
      <div className={styles.caption}>
        <FormattedMessage defaultMessage="Cancel anytime" />
      </div>
    </div>
  );
}
